import useControlledState from "@/hooks/useControlledState";
import { useAddConsumable } from "@/pages/consumables/useConsumables";
import { useQuery } from "@tanstack/react-query";
import { Flex, Form, Input, InputNumber, message, Select, Space, Tag } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import axios from "axios";
import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FormItemContainer from "../FormHeader/FormItemContainer";
import Message from "../Message/Message";
import RootModal from "../RootModal/RootModal";
import ConsumableSelect, { SelectConsumable } from "./ConsumableSelect";

export interface ConsumableSelection {
  id: number;
  name: string;
  amount: number;
  unit: string;
  notes: string;
}

export interface ConsumableForm {
  consumable: SelectConsumable;
  amount: number;
  unit: string;
  notes: string;
}

export interface ConsumableModalProps {
  disabled?: boolean;
  headerText?: string;
  okText?: string;
  value?: ConsumableSelection;
  onChange?: (value?: ConsumableSelection) => void;
  children?: ReactNode;
  resetOnClose?: boolean;
  setIsOpen?: (value: boolean) => void;
  isOpen?: boolean;
  totalConsumabedLabel?: string;
}

export default function ConsumableModal({
  value,
  children,
  disabled = false,
  onChange: onChangeParent,
  resetOnClose = false,
  headerText,
  okText,
  isOpen: parentIsOpen,
  setIsOpen: parentSetIsOpen,
  totalConsumabedLabel = "Total consumed",
}: ConsumableModalProps) {
  const [isOpen, setIsOpen] = useControlledState(false, parentIsOpen, parentSetIsOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = useForm<ConsumableForm>();
  const addConsumable = useAddConsumable();

  const { data: units } = useQuery({
    queryFn: () => axios.get<string[]>("/consumables/units").then((x) => x.data),
    queryKey: ["consumables", "units"],
  });

  const onSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        const newConsumable = {
          name: values.consumable.name,
          notes: values.notes || "",
          amount: values.amount,
          unit: values.unit,
        };

        if (!values.consumable.id) {
          // if this is a new consumable, add it to the db
          const newId = await addConsumable
            .mutateAsync({ name: values.consumable.name })
            .then((x) => x.data.id);

          // let the parent know about the new consumable
          onChangeParent?.({
            id: newId,
            ...newConsumable,
          });
        } else {
          onChangeParent?.({
            id: values.consumable.id,
            ...newConsumable,
          });
        }
        onClose();
      })
      .catch(() => message.error("Saving consumable failed. Fix the errors and try again."));
  };

  const onClose = () => {
    setIsOpen(false);
    if (resetOnClose) form.resetFields();
  };

  const onChangeConsumable = (consumable?: SelectConsumable) => {
    form.setFieldValue("consumable", consumable);
    if (consumable?.units && consumable.units.length > 0) {
      // pre-select the first unit that has been used for this consumable
      form.setFieldValue(["unit"], consumable.units[0]);
    }
  };

  useEffect(() => {
    if (value && value.id && isOpen) {
      setIsLoading(true);
      form.setFieldsValue({ ...value });
      axios.get<SelectConsumable>("/consumable_search/" + value.id).then((x) => {
        form.setFieldValue("consumable", x.data);
        setIsLoading(false);
      });
    }
  }, [value, isOpen]);

  const consumable = useWatch("consumable", form);

  return (
    <>
      {children && <span onClick={() => !disabled && setIsOpen(!isOpen)}>{children}</span>}
      <RootModal
        width={500}
        title={headerText ?? "Add consumable"}
        open={isOpen}
        onCancel={onClose}
        onOk={onSubmit}
        okButtonProps={{ loading: addConsumable.isLoading }}
        okText={okText ?? "Add consumable"}
      >
        <Form
          form={form}
          layout="vertical"
          onKeyUp={(e) => {
            if (e.code === "Enter") onSubmit();
          }}
        >
          <Flex
            vertical
            gap={24}
          >
            <FormItemContainer
              title="Adding a consumable can be done in two ways"
              type="main"
            >
              You can input any consumable name, If you have created consumables before you can
              select them or search for them. Read more about consumables in the{" "}
              <Link
                target="_blank"
                to="https://help.root-sustainability.com/en/articles/11086644-consumables"
              >
                how it works
              </Link>
              .
            </FormItemContainer>
            <FormItemContainer
              title="Consumable"
              type="main"
              isLoading={isLoading}
            >
              <Flex
                vertical
                gap={16}
              >
                <Form.Item
                  name="consumable"
                  noStyle
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <ConsumableSelect
                    value={consumable}
                    onChange={onChangeConsumable}
                  />
                </Form.Item>
                {consumable && !consumable.id && (
                  <Message type="note">
                    You are creating a new consumable. If you want to re-use an existing consumable,
                    search and select it.
                  </Message>
                )}
              </Flex>
            </FormItemContainer>
            <FormItemContainer
              title={totalConsumabedLabel}
              type="main"
            >
              <Flex gap={16}>
                <FormItemContainer
                  title="Amount"
                  isLoading={isLoading}
                  isRequired
                >
                  <Form.Item
                    name="amount"
                    noStyle
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <InputNumber controls={false} />
                  </Form.Item>
                </FormItemContainer>
                <FormItemContainer
                  title="Unit"
                  isLoading={isLoading}
                  isRequired
                >
                  <Form.Item
                    name="unit"
                    noStyle
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <Select
                      showSearch={true}
                      allowClear={true}
                      options={units?.map((x) => ({ value: x, label: x }))}
                      optionRender={(option) => (
                        <Space>
                          {option.label}
                          {option.value && consumable?.units.includes(option.value.toString()) && (
                            <Tag>Used before</Tag>
                          )}
                        </Space>
                      )}
                    />
                  </Form.Item>
                </FormItemContainer>
              </Flex>
            </FormItemContainer>
            <FormItemContainer
              title="Include documentation, sources or used calculations"
              isLoading={isLoading}
              isOptional
            >
              <Form.Item
                name="notes"
                noStyle
              >
                <Input />
              </Form.Item>
            </FormItemContainer>
          </Flex>
        </Form>
      </RootModal>
    </>
  );
}
